import type { ReactFlowInstance } from '@xyflow/react';
import type { DiagramNode, DiagramOpenZoom } from '@ukrop-nm/shared';
import {
  resolvePortCount,
  resolvePortLayout,
  resolveWifiAntennaCount,
  suggestSizeForPorts,
} from '@ukrop-nm/shared';

type ViewportApi = Pick<ReactFlowInstance, 'fitView' | 'setCenter'>;

function nodeDocumentSize(n: DiagramNode): { width: number; height: number } {
  if (n.type === 'zone') {
    return { width: n.size?.width ?? 320, height: n.size?.height ?? 220 };
  }
  return suggestSizeForPorts(
    resolvePortCount(n),
    n.size,
    resolvePortLayout(n),
    resolveWifiAntennaCount(n),
  );
}

export function computeDiagramBounds(nodes: DiagramNode[]): {
  x: number;
  y: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
} | null {
  if (!nodes.length) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const n of nodes) {
    const { width, height } = nodeDocumentSize(n);
    minX = Math.min(minX, n.position.x);
    minY = Math.min(minY, n.position.y);
    maxX = Math.max(maxX, n.position.x + width);
    maxY = Math.max(maxY, n.position.y + height);
  }
  const width = maxX - minX;
  const height = maxY - minY;
  return {
    x: minX,
    y: minY,
    width,
    height,
    centerX: minX + width / 2,
    centerY: minY + height / 2,
  };
}

export function openZoomToFactor(openZoom: DiagramOpenZoom): number {
  return openZoom === 'fit' ? 0.5 : openZoom / 100;
}

/** Initial viewport to avoid 100% flash before centering (x/y refined via setCenter). */
export function defaultViewportForOpenZoom(openZoom: DiagramOpenZoom): { x: number; y: number; zoom: number } {
  const zoom = openZoomToFactor(openZoom);
  return { x: 0, y: 0, zoom };
}

export function applyDiagramViewport(
  rf: ViewportApi,
  openZoom: DiagramOpenZoom,
  documentNodes: DiagramNode[],
): void {
  if (openZoom === 'fit') {
    void rf.fitView({
      padding: 0.06,
      duration: 0,
      includeHiddenNodes: true,
      minZoom: 0.05,
      maxZoom: 2,
    });
    return;
  }
  const zoom = openZoom / 100;
  const bounds = computeDiagramBounds(documentNodes);
  if (!bounds) {
    rf.setCenter(0, 0, { zoom, duration: 0 });
    return;
  }
  rf.setCenter(bounds.centerX, bounds.centerY, { zoom, duration: 0 });
}

/** Apply opening zoom once after the canvas is ready. */
export function scheduleDiagramViewport(
  rf: ViewportApi,
  openZoom: DiagramOpenZoom,
  documentNodes: DiagramNode[],
  onApplied?: () => void,
): () => void {
  let cancelled = false;
  let timeoutId = 0;

  const run = () => {
    if (cancelled) return;
    applyDiagramViewport(rf, openZoom, documentNodes);
    onApplied?.();
  };

  if (openZoom === 'fit') {
    let attempts = 0;
    const maxAttempts = 24;
    const tick = () => {
      if (cancelled) return;
      attempts += 1;
      if (attempts < maxAttempts) {
        timeoutId = window.setTimeout(tick, 80);
        return;
      }
      run();
    };
    timeoutId = window.setTimeout(tick, 160);
  } else {
    timeoutId = window.setTimeout(run, 0);
  }

  return () => {
    cancelled = true;
    window.clearTimeout(timeoutId);
  };
}
